import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Briefcase, Code2, FileText, Flame, ChevronRight, History, LayoutGrid } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { ChoiceCard } from '#/components/plan/choice-card'
import { PlanDrawer } from '#/components/plan/plan-drawer'
import { PlanLink } from '#/components/common/plan-link'
import { usePlansWhen } from '#/lib/api-ws'
import { authClient } from '#/lib/auth-client'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/home')({ component: Home })

function Home() {
  return (
    <>
      <AppHeader />
      <main className="flex flex-1 flex-col overflow-y-auto">
        {/* Hero with looping video background (poster shown when reduced motion is preferred) */}
        <section className="hero-video relative flex min-h-[46vh] items-center justify-center overflow-hidden px-6 py-16 text-center text-white sm:min-h-[52vh]">
          <video className="hero-video__media absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline preload="metadata" poster="/media/hero-poster.jpg" aria-hidden>
            <source src="/media/hero-1080.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-background" aria-hidden />
          <div className="relative flex flex-col items-center gap-4">
            <Link
              to="/pricing"
              search={{ voucher: undefined }}
              className="inline-flex items-center gap-1 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur hover:bg-white/20"
            >
              <Flame className="size-3.5 text-amber-300" /> {m.landing_promo()} <ChevronRight className="size-3" />
            </Link>
            <h1 className="text-4xl font-bold drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)] sm:text-6xl">{m.landing_title()}</h1>
            <p className="max-w-xl text-base text-white/80 drop-shadow sm:text-lg">{m.tagline()}</p>
          </div>
        </section>
        <div className="mx-auto w-full max-w-5xl space-y-8 px-6 py-10">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ChoiceCard
              to="/plan"
              icon={<FileText className="size-5" />}
              tone="green"
              badge={m.landing_card_plan_badge()}
              title={m.landing_card_plan_title()}
              description={m.landing_card_plan_desc()}
            />
            <ChoiceCard
              to="/plan"
              search={{ flow: 'existing' }}
              icon={<Code2 className="size-5" />}
              tone="amber"
              title={m.landing_card_codebase_title()}
              description={m.landing_card_codebase_desc()}
            />
            <ChoiceCard
              to="/room"
              icon={<LayoutGrid className="size-5" />}
              tone="violet"
              badge={m.dr_home_card_badge()}
              title={m.dr_home_card_title()}
              description={m.dr_home_card_desc()}
            />
            <ChoiceCard
              to="/consulting"
              icon={<Briefcase className="size-5" />}
              tone="blue"
              title={m.landing_card_consulting_title()}
              description={m.landing_card_consulting_desc()}
            />
          </div>
          <ContinueRow />
          <section className="rounded-2xl border bg-card/60 p-5">
            <h2 className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{m.landing_how_title()}</h2>
            <ol className="grid gap-4 sm:grid-cols-3">
              {[
                [m.landing_how_1_title(), m.landing_how_1_desc()],
                [m.landing_how_2_title(), m.landing_how_2_desc()],
                [m.landing_how_3_title(), m.landing_how_3_desc()],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-3">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-primary-foreground">{i + 1}</span>
                  <div>
                    <div className="font-heading font-semibold">{t}</div>
                    <p className="text-sm text-muted-foreground">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </main>
      <footer className="flex h-10 shrink-0 items-center justify-center gap-4 border-t text-xs text-muted-foreground">
        <Link to="/docs" className="hover:text-foreground">{m.landing_footer_docs()}</Link>
        <Link to="/pricing" search={{ voucher: undefined }} className="hover:text-foreground">{m.landing_footer_pricing()}</Link>
        <Link to="/support" className="hover:text-foreground">{m.landing_footer_support()}</Link>
        <span>·</span>
        <span className="wordmark">by<span className="dot">·</span>step</span>
      </footer>
    </>
  )
}

/** "Pick up where you left off": last 3 plans of a signed-in user, straight under the four cards. */
function ContinueRow() {
  const { data: session } = authClient.useSession()
  const { data } = usePlansWhen(Boolean(session?.user))
  const [drawer, setDrawer] = useState(false)
  if (!session?.user) return null
  const plans = [...(data?.plans ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 3)
  if (!plans.length) return null
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><History className="size-3.5" /> {m.ws_continue_title()}</h2>
        <button className="text-xs text-primary hover:underline" onClick={() => setDrawer(true)}>{m.ws_continue_all()}</button>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {plans.map((p) => (
          <PlanLink key={p.id} plan={p} className="group flex min-w-0 items-center gap-3 rounded-xl border bg-card px-3 py-2.5 text-sm hover:border-primary/60">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"><FileText className="size-4" /></span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{p.title}</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {p.status === 'generating' ? <span className="text-status-progress">{m.ws_plan_generating()}</span> : p.status !== 'completed' ? m.ws_plan_draft() : p.workspaceName} · {fmtDate(p.updatedAt)}
              </span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground group-hover:text-primary" />
          </PlanLink>
        ))}
      </div>
      <PlanDrawer open={drawer} onOpenChange={setDrawer} />
    </section>
  )
}
